import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { compactOverview } from '@/api/explorer';
import { isLoaded } from '@/interfaces/RemoteData';
import { FILTERABLE_COLUMNS, getDefaultFilterValue } from './explorerColumns';

// TypeScript Interface für Filter-Spalten
interface FilterColumn {
  key: string;
  label: string;
  filterable: boolean;
  min: number;
  max: number;
}

// Helper-Funktionen für Filter-Validierung
export function validateFilterRange(
  key: string,
  value: [number, number],
  minMaxValues: Record<string, { min: number; max: number }>,
): [number, number] {
  const [min, max] = value;
  const column = FILTERABLE_COLUMNS.find(
    (col) => col.key === key,
  );
  if (!column) return [min, max];

  const minAllowed = minMaxValues[key]?.min ?? column.min ?? 0;
  const maxAllowed = minMaxValues[key]?.max ?? column.max ?? 0;

  let validMin = Math.max(min, minAllowed);
  let validMax = Math.min(max, maxAllowed);

  if (validMin > validMax) validMax = validMin;
  if (validMax < validMin) validMin = validMax;

  return [validMin, validMax];
}

// Helper-Funktionen für Spalten-Zugriff
export function getSuraColumn(): FilterColumn | undefined {
  const column = FILTERABLE_COLUMNS.find((col) => col.key === 'sura');
  if (!column) return undefined;
  return {
    key: column.key,
    label: column.labelKey,
    filterable: column.isFilterable || false,
    min: column.min || 0,
    max: column.max || 0,
  };
}

export function getVerseColumn(): FilterColumn | undefined {
  const column = FILTERABLE_COLUMNS.find((col) => col.key === 'verse');
  if (!column) return undefined;
  return {
    key: column.key,
    label: column.labelKey,
    filterable: column.isFilterable || false,
    min: column.min || 0,
    max: column.max || 0,
  };
}

export function getStatColumn(key: string): FilterColumn | undefined {
  const column = FILTERABLE_COLUMNS.find((col) => col.key === key);
  if (!column) return undefined;
  return {
    key: column.key,
    label: column.labelKey,
    filterable: column.isFilterable || false,
    min: column.min || 0,
    max: column.max || 0,
  };
}

export default function useExplorerFilters(): {
  filterColumns: any;
  filterValues: any;
  updateFilter: (key: string, value: [number, number]) => void;
  resetFilters: () => void;
  } {
  const { t } = useI18n();

  // Dynamische Filterwerte
  const filterValues = ref<Record<string, [number, number]>>({});

  // Performance-optimierte Berechnung mit Caching
  const minMaxValues = computed(() => {
    if (!isLoaded(compactOverview.value) || !compactOverview.value.payload) {
      const fallback: Record<string, { min: number; max: number }> = {};
      FILTERABLE_COLUMNS.forEach((column) => {
        fallback[column.key] = { min: 0, max: 0 };
      });
      return fallback;
    }

    const compactData = compactOverview.value.payload;
    const result: Record<string, { min: number; max: number }> = {};

    // Verwende direkte Property-Zugriffe für bessere Performance
    FILTERABLE_COLUMNS.forEach((column) => {
      let minValue = Number.POSITIVE_INFINITY;
      let maxValue = Number.NEGATIVE_INFINITY;

      // Optimierte Schleife mit direkten Zugriffen
      for (let i = 0; i < compactData.length; i += 1) {
        const row = compactData[i];
        let value: number;

        // Direkte Property-Zugriffe statt Switch
        switch (column.key) {
          case 'sura': value = row.s; break;
          case 'verse': value = row.v; break;
          case 'readings_count': value = row.r; break;
          case 'references_count': value = row.rf; break;
          case 'sources_count': value = row.sr; break;
          case 'variants_count': value = row.vr; break;
          case 'manuscripts_count': value = row.m; break;
          case 'intertexts_count': value = row.i; break;
          default: value = 0;
        }

        if (value < minValue) minValue = value;
        if (value > maxValue) maxValue = value;
      }

      if (minValue === Number.POSITIVE_INFINITY) minValue = 0;
      if (maxValue === Number.NEGATIVE_INFINITY) maxValue = 0;
      result[column.key] = { min: minValue, max: maxValue };
    });
    return result;
  });

  const filterColumns = computed<FilterColumn[]>(() => FILTERABLE_COLUMNS.map((column) => {
    const min = minMaxValues.value[column.key]?.min ?? column.min ?? 0;
    const max = minMaxValues.value[column.key]?.max ?? column.max ?? 0;
    return {
      key: column.key,
      label: t(column.labelKey),
      filterable: true,
      min,
      max,
    };
  }));

  const initializeFilters = () => {
    FILTERABLE_COLUMNS.forEach((column) => {
      const min = minMaxValues.value[column.key]?.min ?? column.min ?? 0;
      const max = minMaxValues.value[column.key]?.max ?? column.max ?? 0;
      // Verwende die zentrale Helper-Funktion für Standard-Werte
      filterValues.value[column.key] = getDefaultFilterValue(column, max);
      // Falls der Default-Min außerhalb des echten Bereichs liegt, korrigiere ihn
      if (filterValues.value[column.key][0] < min) filterValues.value[column.key][0] = min;
      if (filterValues.value[column.key][1] > max) filterValues.value[column.key][1] = max;
    });
  };

  watch(filterColumns, initializeFilters, { immediate: true });

  const updateFilter = (key: string, value: [number, number]) => {
    filterValues.value[key] = validateFilterRange(key, value, minMaxValues.value);
  };

  const resetFilters = () => {
    initializeFilters();
  };

  return {
    filterColumns,
    filterValues,
    updateFilter,
    resetFilters,
  };
}
